<script setup lang="ts">
import { dashboardApiClient } from '@api-client/dashboard';
import type { ResType } from '@api-client/typeUtils';
import { pageTitle } from '@partner-spa-vue/stores/LayoutStore';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { onMounted, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-vue-next';
import { toast } from '@/ui-plus/sonner';
import type { ServiceFee } from '@lib/zod/serviceFee';
import { zmgAdminOnlyCacheUpdateClient, zmgPublicCacheClient } from '@api-client/zmg_cache';
import StoreDetails from '../StoreDetails.vue';

type StoresResponse = ResType<
    typeof dashboardApiClient.website.manage_stores.list[':websiteId']['$get']
>["data"];

pageTitle.value = 'Listed Stores';

const stores = ref<StoresResponse['stores']>([]);
const loading = ref(false);
const hasMore = ref(false);
const limit = ref(10);
const cursorStack = ref<string[]>([]);
const currentCursor = ref<string | undefined>(undefined);
const nextCursor = ref<string | undefined>(undefined);
const searchQuery = ref('');

const loadStores = async (cursor?: string) => {
    loading.value = true;
    const { data, error } = await (await dashboardApiClient.website.manage_stores.list[':websiteId'].$get({
        param: { websiteId: currentWebsiteId.value ?? '' },
        query: {
            limit: String(limit.value),
            cursor,
            search: searchQuery.value || undefined
        }
    })).json();

    if (error) {
        console.error(error);
        toast.error('Failed to load stores');
    } else if (data) {
        stores.value = data.stores;
        hasMore.value = data.pagination.hasMore;
        nextCursor.value = data.pagination.nextCursor ?? undefined;
        currentCursor.value = cursor;
    }
    loading.value = false;
};

const handleSearch = () => {
    cursorStack.value = [];
    loadStores(undefined);
};

const handleNext = () => {
    if (!nextCursor.value) return;
    if (currentCursor.value !== undefined) {
        cursorStack.value.push(currentCursor.value);
    }
    loadStores(nextCursor.value);
};

const handlePrev = () => {
    const prevCursor = cursorStack.value.pop();
    loadStores(prevCursor);
};



onMounted(async () => {
    loadMarketplaceMode();
    loadStores();
});

const isListingAllStores = ref(false);
const defaultMarketplaceFee = ref<ServiceFee>({
    ratePerOrder: 0,
    feePerOrder: 0
});

const loadMarketplaceMode = async () => {
    const { data, error } = await (await zmgPublicCacheClient.website.partner_info[':id']['data.json'].$get({
        param: { id: currentWebsiteId.value ?? '' },
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to load marketplace mode');
    } else if (data) {
        isListingAllStores.value = data.isListingAllStores;
        defaultMarketplaceFee.value = data.defaultMarketplaceFee || {
            ratePerOrder: 0,
            feePerOrder: 0
        };
    }
}

const onMarketplaceModeChange = async (checked: boolean) => {
    isListingAllStores.value = checked;
    await saveChanges();
}

const saveChanges = async () => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.partner_info[':id'].$patch({
        param: { id: currentWebsiteId.value ?? '' },
        json: {
            isListingAllStores: isListingAllStores.value,
            defaultMarketplaceFee: defaultMarketplaceFee.value,
        }
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to update marketplace mode');
    } else {
        toast.success('Marketplace settings updated');
    }
}

</script>

<template>
    <div class="space-y-6">
        <div class="flex items-center justify-between p-4 border rounded-lg cursor-pointer">
            <div class="space-y-0.5">
                <h3 class="text-lg font-medium">
                    Listing Mode ({{ isListingAllStores ?
                        'All active stores' :
                        'Selected only' }})
                </h3>

                <p class="text-muted-foreground">Do you want to allow all available stores to be listed on
                    your
                    website?</p>
                <p class="text-muted-foreground">({{ isListingAllStores ?
                    'All available stores are listed' :
                    'Only selected stores below are listed' }})</p>
            </div>
            <div class="flex items-center space-x-3">
                <Switch id="marketplaceMode"
                        :checked="isListingAllStores"
                        @update:checked="onMarketplaceModeChange"
                        class="scale-125">
                </Switch>
                <span class="font-medium">{{ isListingAllStores ? 'All' : 'Selected' }}</span>
            </div>
        </div>

        <!-- Search bar -->
        <div class="flex gap-2">
            <div class="relative flex-1">
                <Search class="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input type="search"
                       placeholder="Search stores..."
                       v-model="searchQuery"
                       class="pl-8"
                       @keyup.enter="handleSearch" />
            </div>
            <Button @click="handleSearch">Search</Button>
        </div>

        <!-- Stores list -->
        <div v-if="!loading"
             class="space-y-4">
            <div v-for="store in stores"
                 :key="store.id"
                 class="flex items-center justify-between p-4 border rounded-lg">
                <StoreDetails v-if="currentWebsiteId"
                              :store="store"
                              :currentWebsiteId="currentWebsiteId!"
                              :isListingAllStores="isListingAllStores"
                              :isServiceFeeEditable="true" />
            </div>

            <!-- Empty state -->
            <div v-if="stores.length === 0"
                 class="p-8 text-center text-muted-foreground">
                No stores found. Try adjusting your search.
            </div>
        </div>

        <!-- Loading state -->
        <div v-else
             class="p-8 text-center text-muted-foreground">
            Loading stores...
        </div>

        <!-- Pagination -->
        <div v-if="cursorStack.length > 0 || hasMore"
             class="flex justify-center gap-2 mt-4">
            <Button variant="outline"
                    :disabled="cursorStack.length === 0"
                    @click="handlePrev">Previous</Button>
            <Button variant="outline"
                    :disabled="!hasMore"
                    @click="handleNext">Next</Button>
        </div>
    </div>
</template>